import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useResendVerification, { isKnownUnverified } from '../../hooks/useResendVerification';
import CloseButton from './CloseButton';

// One line, and ✕ quiets it for a week (2026-10-01). It was ~80px at the top
// of every page on a phone and came back every session. The phone menu (/you)
// keeps a "Verify your email" row until the address is verified, so hiding
// the banner never hides the way to do it.
const DISMISS_DAYS = 7;
const dismissKey = (userId) => `verifyEmailBannerDismissedUntil:${userId}`;

function readDismissed(userId) {
  try {
    const until = Number(localStorage.getItem(dismissKey(userId)));
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false; // storage blocked: show the banner
  }
}

function VerifyEmailBanner() {
  const { currentUser } = useAuth();
  const { t } = useTranslation('auth');
  // Keyed per account, so a second account on the device gets its own nudge
  const [dismissedFor, setDismissedFor] = useState(null);
  const { sendState, resend } = useResendVerification();

  if (!isKnownUnverified(currentUser)) return null;
  if (dismissedFor === currentUser.id || readDismissed(currentUser.id)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey(currentUser.id), String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    } catch { /* blocked: hidden for this page load only */ }
    setDismissedFor(currentUser.id);
  };

  return (
    <div className="bg-warning-light border-b border-warning/20 px-4" role="status">
      <div className="max-w-5xl mx-auto flex items-center gap-2 text-sm min-h-[40px]">
        <p className="flex-1 min-w-0 truncate text-warning-dark">
          {sendState === 'sent' ? t('verifyBanner.sent') : t('verifyBanner.message')}
        </p>
        {sendState !== 'sent' && (
          <button
            type="button"
            onClick={resend}
            disabled={sendState === 'sending'}
            className="flex-shrink-0 min-h-[36px] px-1 font-semibold text-warning-dark underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light rounded"
          >
            {sendState === 'sending'
              ? t('verifyBanner.sending')
              : sendState === 'error' ? t('verifyBanner.retry') : t('verifyBanner.resend')}
          </button>
        )}
        <CloseButton
          variant="plain"
          size="sm"
          onClick={dismiss}
          ariaLabel={t('verifyBanner.dismiss')}
          className="-mr-2 -my-1 text-warning-dark hover:text-warning-dark"
        />
      </div>
    </div>
  );
}

export default VerifyEmailBanner;
